import React, { memo, useState, useEffect } from 'react';
import { Box, Typography, Collapse } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { TbChevronDown, TbChevronUp } from 'react-icons/tb';
import Popover from '@/shared/ui/popover/Popover';
import type { ISidebarSubmenuProps, ISidebarRoute } from '../types/sidebar.types';

/**
 * Sidebar submenu component for nested menu items
 * Handles expansion/collapse and rendering of child items
 */
const SidebarSubmenu = memo<ISidebarSubmenuProps>(({
    parentItem,
    isExpanded,
    onToggle,
    onItemClick,
}) => {
    const { t } = useTranslation();
    const location = useLocation();
    const [isSubmenuExpanded, setIsSubmenuExpanded] = useState(false);

    // Auto-expand submenu if any child is active
    useEffect(() => {
        const hasActiveChild = parentItem.items?.some(childItem => {
            if (!childItem.path) return false;
            return location.pathname === childItem.path ||
                location.pathname.startsWith(childItem.path + '/');
        });

        if (hasActiveChild) {
            setIsSubmenuExpanded(true);
        }
    }, [location.pathname, parentItem.items]);

    /**
     * Check if parent item is active (any child is active)
     */
    const isParentActive = parentItem.items?.some(childItem => {
        if (!childItem.path) return false;
        return location.pathname === childItem.path ||
            location.pathname.startsWith(childItem.path + '/');
    }) || false;

    /**
     * Check if a child item is active
     */
    const isChildActive = (childItem: ISidebarRoute): boolean => {
        if (!childItem.path) return false;
        return location.pathname === childItem.path ||
            location.pathname.startsWith(childItem.path + '/');
    };

    /**
     * Handle submenu toggle
     */
    const handleToggle = () => {
        const newExpandedState = !isSubmenuExpanded;
        setIsSubmenuExpanded(newExpandedState);
        onToggle();
    };

    /**
     * Handle submenu item click
     */
    const handleItemClick = (item: ISidebarRoute, event?: React.MouseEvent) => {
        if (event) {
            event.stopPropagation();
        }
        onItemClick(item);
    };

    /**
     * Render expanded submenu with parent and children
     */
    const renderExpandedSubmenu = () => (
        <>
            {/* Parent item */}
            <Box
                sx={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '8px 12px',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    fontSize: '15px',
                    fontWeight: isParentActive ? 700 : 500,
                    color: isParentActive ? '#FFFFFF' : '#374151',
                    cursor: 'pointer',
                    borderRadius: '12px',
                    background: isParentActive
                        ? 'linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)'
                        : 'transparent',
                    boxShadow: isParentActive
                        ? '0 8px 32px rgba(59, 130, 246, 0.3)'
                        : 'none',
                    '&:hover': {
                        background: isParentActive
                            ? 'linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)'
                            : 'linear-gradient(135deg, #F1F5F9 0%, #E2E8F0 100%)',
                        color: isParentActive ? '#FFFFFF' : '#1E293B',
                        transform: isParentActive ? 'translateY(-1px)' : 'translateX(4px)',
                        boxShadow: isParentActive
                            ? '0 12px 40px rgba(59, 130, 246, 0.4)'
                            : '0 4px 16px rgba(0, 0, 0, 0.1)',
                    }
                }}
                onClick={handleToggle}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 20,
                            height: 20,
                            fontSize: '16px',
                            color: isParentActive ? '#FFFFFF' : '#374151'
                        }}>
                            {parentItem.icon}
                        </Box>
                        <Typography sx={{
                            fontSize: '14px',
                            fontWeight: isParentActive ? 600 : 500,
                            color: isParentActive ? '#FFFFFF' : '#374151',
                            flex: 1,
                            textAlign: 'left'
                        }}>
                            {t(`SIDEBAR.${parentItem.translate}`)}
                        </Typography>
                    </Box>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 16,
                        height: 16,
                        color: isParentActive ? '#FFFFFF' : '#374151',
                        transform: isSubmenuExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s ease'
                    }}>
                        {isSubmenuExpanded ? <TbChevronUp size={16} /> : <TbChevronDown size={16} />}
                    </Box>
                </Box>
            </Box>

            {/* Submenu items */}
            <Collapse in={isSubmenuExpanded} timeout="auto" unmountOnExit>
                <Box sx={{
                    marginLeft: '20px',
                    marginRight: '8px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '2px',
                    paddingTop: '6px',
                    paddingBottom: '4px',
                    borderLeft: '2px solid #E2E8F0',
                    position: 'relative',
                    width: 'calc(100% - 20px)',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: '-2px',
                        top: 0,
                        bottom: 0,
                        width: '2px',
                        background: 'linear-gradient(180deg, #3B82F6 0%, rgba(59, 130, 246, 0.3) 100%)',
                        borderRadius: '1px',
                    }
                }}>
                    {parentItem.items?.map((childItem, index) => {
                        const childActive = isChildActive(childItem);
                        return (
                            <Box
                                key={childItem.id || index}
                                sx={{
                                    width: '100%',
                                    minWidth: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px',
                                    padding: '8px 12px',
                                    marginRight: '8px',
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    fontSize: '14px',
                                    fontWeight: childActive ? 600 : 500,
                                    color: childActive ? '#3B82F6' : '#6B7280',
                                    cursor: 'pointer',
                                    borderRadius: '10px',
                                    background: childActive
                                        ? 'linear-gradient(135deg, #EBF4FF 0%, #DBEAFE 100%)'
                                        : 'transparent',
                                    // borderLeft: childActive ? '2px solid #3B82F6' : '2px solid transparent',
                                    boxShadow: childActive
                                        ? '0 4px 16px rgba(59, 130, 246, 0.15)'
                                        : 'none',
                                    '&:hover': {
                                        background: childActive
                                            ? 'linear-gradient(135deg, #EBF4FF 0%, #DBEAFE 100%)'
                                            : 'linear-gradient(135deg, #F8FAFC 0%, #F1F5F9 100%)',
                                        color: childActive ? '#3B82F6' : '#374151',
                                        transform: 'translateX(4px)',
                                        boxShadow: childActive
                                            ? '0 6px 20px rgba(59, 130, 246, 0.2)'
                                            : '0 2px 8px rgba(0, 0, 0, 0.1)',
                                    }
                                }}
                                onClick={(e) => handleItemClick(childItem, e)}
                            >

                                <Typography sx={{
                                    fontSize: '14px',
                                    fontWeight: childActive ? 600 : 500,
                                    color: 'inherit',
                                    flex: 1,
                                    whiteSpace: 'nowrap',
                                    overflow: 'visible',
                                    textOverflow: 'clip',
                                    textAlign: 'left'
                                }}>
                                    {t(`SIDEBAR.${childItem.translate}`)}
                                </Typography>
                            </Box>
                        );
                    })}
                </Box>
            </Collapse>
        </>
    );

    /**
     * Render collapsed submenu – SidebarMenuItem bilan bir xil tartibli icon qutisi
     */
    const renderCollapsedSubmenu = () => (
        <Popover
            placement="right"
            content={
                <Box sx={{
                    width: 200,
                    padding: '12px 14px',
                    backgroundColor: '#FFFFFF',
                    borderRadius: '12px',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                    border: '1px solid #E2E8F0',
                }}>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '8px 0 10px',
                        borderBottom: '1px solid #E2E8F0',
                        marginBottom: '8px',
                    }}>
                        <Box sx={{ fontSize: '18px', color: '#374151' }}>{parentItem.icon}</Box>
                        <Typography variant="body2" sx={{ fontSize: '13px', fontWeight: 600, color: '#374151' }}>
                            {t(`SIDEBAR.${parentItem.translate}`)}
                        </Typography>
                    </Box>
                    {parentItem.items?.map((childItem, index) => {
                        const childActive = isChildActive(childItem);
                        return (
                            <Box
                                key={childItem.id || index}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    padding: '8px 10px',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    color: childActive ? '#3B82F6' : '#475569',
                                    fontWeight: childActive ? 600 : 500,
                                    backgroundColor: childActive ? 'rgba(59, 130, 246, 0.08)' : 'transparent',
                                    '&:hover': {
                                        backgroundColor: childActive ? 'rgba(59, 130, 246, 0.12)' : '#F1F5F9',
                                        color: childActive ? '#2563EB' : '#1E293B',
                                    },
                                }}
                                onClick={() => handleItemClick(childItem)}
                            >
                                <Box sx={{
                                    width: 6,
                                    height: 6,
                                    borderRadius: '50%',
                                    backgroundColor: childActive ? '#3B82F6' : '#94A3B8',
                                    flexShrink: 0,
                                }} />
                                <Typography variant="body2" sx={{ fontSize: '13px', fontWeight: 'inherit' }}>
                                    {t(`SIDEBAR.${childItem.translate}`)}
                                </Typography>
                            </Box>
                        );
                    })}
                </Box>
            }
        >
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 40,
                height: 40,
                minWidth: 40,
                borderRadius: '12px',
                fontSize: '18px',
                color: isParentActive ? '#FFFFFF' : '#475569',
                background: isParentActive
                    ? 'linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)'
                    : 'transparent',
                boxShadow: isParentActive ? '0 4px 12px rgba(59, 130, 246, 0.35)' : 'none',
                transition: 'all 0.2s ease',
                '&:hover': {
                    background: isParentActive
                        ? 'linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)'
                        : '#F1F5F9',
                    color: isParentActive ? '#FFFFFF' : '#1E293B',
                },
            }}>
                {parentItem.icon}
            </Box>
        </Popover>
    );

    return (
        <Box
            sx={
                !isExpanded
                    ? {
                          display: 'flex',
                          justifyContent: 'center',
                          padding: '6px 0',
                          position: 'relative',
                          ...(isParentActive && {
                              '&::before': {
                                  content: '""',
                                  position: 'absolute',
                                  left: 0,
                                  top: '50%',
                                  transform: 'translateY(-50%)',
                                  width: '3px',
                                  height: '20px',
                                  background: 'linear-gradient(180deg, #3B82F6 0%, #1D4ED8 100%)',
                                  borderRadius: '0 2px 2px 0',
                              },
                          }),
                      }
                    : undefined
            }
        >
            {isExpanded ? renderExpandedSubmenu() : renderCollapsedSubmenu()}
        </Box>
    );
});

SidebarSubmenu.displayName = 'SidebarSubmenu';

export default SidebarSubmenu;
